import type { Ayah } from '@/types/quran';

export type OneDayLanguage = 'th' | 'en';

export interface OneDayOneAyahState {
  ayah: Ayah;
  meaningLanguage: OneDayLanguage;
  showArabic: boolean;
}

const STORAGE_KEY_AYAH_PREFIX = 'oneDayOneAyah:ayah:';
const STORAGE_KEY_DISMISSED_PREFIX = 'oneDayOneAyah:dismissed:';

function getTodayDateKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getAyahStorageKey(dateKey: string = getTodayDateKey()): string {
  return `${STORAGE_KEY_AYAH_PREFIX}${dateKey}`;
}

function getDismissedStorageKey(dateKey: string = getTodayDateKey()): string {
  return `${STORAGE_KEY_DISMISSED_PREFIX}${dateKey}`;
}

export function getTodayOneDayOneAyah(): OneDayOneAyahState | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = window.localStorage.getItem(getAyahStorageKey());
    if (!raw) return null;
    return JSON.parse(raw) as OneDayOneAyahState;
  } catch (error) {
    console.error('[oneDayOneAyah] Error reading today ayah from storage:', error);
    return null;
  }
}

export function saveTodayOneDayOneAyah(state: OneDayOneAyahState): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(getAyahStorageKey(), JSON.stringify(state));
  } catch (error) {
    console.error('[oneDayOneAyah] Error saving today ayah to storage:', error);
  }
}

export function clearTodayOneDayOneAyah(): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(getAyahStorageKey());
  } catch (error) {
    console.error('[oneDayOneAyah] Error clearing today ayah from storage:', error);
  }
}

export function isTodayDismissed(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(getDismissedStorageKey()) === '1';
  } catch (error) {
    console.error('[oneDayOneAyah] Error reading dismissed flag from storage:', error);
    return false;
  }
}

export function markTodayDismissed(): void {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(getDismissedStorageKey(), '1');
  } catch (error) {
    console.error('[oneDayOneAyah] Error saving dismissed flag to storage:', error);
  }
}
